import { useState, useEffect, useCallback, useRef } from 'react';
import { listChats, createChatSession, deleteChatById, renameChatById } from '../api';
import type { ChatListing } from '../api/model';

const DEFAULT_CHAT_TITLE = 'Основной чат';

export { DEFAULT_CHAT_TITLE };

export interface UseChatListResult {
  chats: ChatListing[];
  activeChatId: string | null;
  isLoadingChats: boolean;
  switchChat: (id: string) => void;
  createChat: (title?: string) => Promise<ChatListing | undefined>;
  deleteChat: (id: string) => Promise<void>;
  renameChat: (id: string, title: string) => Promise<void>;
}

export function useChatList(projectId: string): UseChatListResult {
  const [chats, setChats] = useState<ChatListing[]>([]);
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [isLoadingChats, setIsLoadingChats] = useState(false);

  // Always-fresh reference to avoid stale closures in callbacks
  const chatsRef = useRef(chats);
  chatsRef.current = chats;

  useEffect(() => {
    if (!projectId) return;
    setIsLoadingChats(true);
    listChats(projectId)
      .then((items) => {
        setChats(items);
        setActiveChatId(items.length > 0 ? items[0].id : null);
      })
      .catch(() => undefined)
      .finally(() => setIsLoadingChats(false));
  }, [projectId]);

  const switchChat = useCallback((id: string) => {
    setActiveChatId(id);
  }, []);

  const createChat = useCallback(
    async (title?: string): Promise<ChatListing | undefined> => {
      if (!projectId) return undefined;
      const chat = await createChatSession(projectId, title);
      if (chat) {
        setChats((prev) => [chat, ...prev]);
        setActiveChatId(chat.id);
      }
      return chat;
    },
    [projectId]
  );

  const deleteChat = useCallback(async (id: string): Promise<void> => {
    if (chatsRef.current.length <= 1) return;
    await deleteChatById(id);
    const next = chatsRef.current.filter((c) => c.id !== id);
    setChats(next);
    setActiveChatId((current) => (current === id ? (next[0]?.id ?? null) : current));
  }, []);

  const renameChat = useCallback(async (id: string, title: string): Promise<void> => {
    const updated = await renameChatById(id, title);
    if (updated) {
      setChats((prev) => prev.map((c) => (c.id === id ? updated : c)));
    }
  }, []);

  return { chats, activeChatId, isLoadingChats, switchChat, createChat, deleteChat, renameChat };
}
